/* ==========================================================================
   accessCodeService

   REAL MODE: a resident never writes to the access_codes table directly —
   creation goes through the resident_create_access_code Postgres function
   (security definer), which stamps the caller's own estate_id, generates a
   unique code, and enforces the one_time-needs-an-expiry rule server side.
   Listing reads the table directly (RLS already scopes it to the caller's
   estate — see access_codes_select_own_estate in supabase/schema.sql).

   Verifying and checking a visitor in/out, and granting the "security"
   role, are gate/admin actions — see adminService.ts, not this file.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapAccessCodeRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay } from "./mock/mockUtils";
import { requireActiveEstateId } from "../state/estateContext";
import type { AccessCode, CreateAccessCodeRequest } from "../types";

async function listMyAccessCodes(): Promise<AccessCode[]> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(300);
    return mockDb.getAccessCodes(estateId);
  }

  const { data, error } = await getSupabase()
    .from("access_codes")
    .select("*")
    .eq("estate_id", estateId)
    .order("created_at", { ascending: false })
    .limit(30);
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapAccessCodeRow);
}

async function createAccessCode(req: CreateAccessCodeRequest): Promise<AccessCode> {
  const estateId = requireActiveEstateId();

  if (!req.visitorName.trim()) {
    throw new Error("Enter the visitor's name.");
  }
  if (req.codeType === "one_time" && !req.expiresAt) {
    throw new Error("Choose when this one-time code should expire.");
  }

  if (config.useMockServices) {
    await delay(500);
    return mockDb.createAccessCode(estateId, {
      visitorName: req.visitorName.trim(),
      category: req.category,
      gender: req.gender,
      maxPeople: req.maxPeople,
      description: req.description,
      codeType: req.codeType,
      expiresAt: req.expiresAt,
    });
  }

  const { data, error } = await getSupabase().rpc("resident_create_access_code", {
    p_visitor_name: req.visitorName.trim(),
    p_category: req.category || null,
    p_gender: req.gender || null,
    p_max_people: req.maxPeople,
    p_expires_at: req.expiresAt ?? null,
    p_description: req.description || null,
    p_code_type: req.codeType,
  });
  if (error) throw new Error(error.message);
  return mapAccessCodeRow(data);
}

export const accessCodeService = { listMyAccessCodes, createAccessCode };
